/**
 * Backend Call Graph — first-class artifact representing the backend half
 * of the E2E flow analyzer. See veodiagram issue #807 (Phase 2.1 of the
 * stitching accuracy initiative, epic #802).
 *
 * The graph has four node kinds:
 *   - RouteEndpoint — an API endpoint (HTTP method + fully composed path)
 *   - Function     — an intermediate server-side function on the path from
 *                    a route handler to a sink (DB/external call)
 *   - DbCall       — a database query leaf (Prisma, TypeORM, Mongoose, ...)
 *   - ExternalCall — a third-party call leaf (Stripe, Supabase auth, ...)
 *
 * Edges are `routes-to` (RouteEndpoint -> Function) or `calls` (anything ->
 * anything downstream).
 *
 * Baseline (this issue): builder is a pure refactor. It reuses the outputs
 * of the existing extractors (`routeMatcher` route discovery, `callGraph`,
 * `executionPathTracer` for DB queries and external calls) to produce a
 * graph that is a superset of what Tier-1 already tracks today. Framework
 * prefix composition (#808) and golden tests (#809) follow.
 */
import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Structural keying (used by #810 StitchSet) =============
/**
 * Structural key for a backend route endpoint that survives refactors.
 * Phase 3 (#810) uses this to reattach user-authored stitches after
 * re-analysis even when files move or handlers are renamed.
 */
export const RouteKeySchema = z.object({
    framework: z.string(),
    /** HTTP method normalized uppercase, or 'ANY' for multi-method routes. */
    method: z.string(),
    /** Fully composed path (#808 resolves register prefixes etc.). */
    path: z.string(),
    /** Enclosing handler function name, e.g. 'getUser'. */
    handlerSymbol: z.string(),
    /** Repo-relative source file, normalized with forward slashes. */
    filePath: z.string(),
});
// ============= Route endpoint =============
export const RouteEndpointSchema = z.object({
    /** Unique within this graph. */
    routeId: z.string(),
    /** Structural key for cross-analysis stability. */
    routeKey: RouteKeySchema,
    /** HTTP method, uppercase, or 'ANY' for multi-method endpoints. */
    method: z.string(),
    /** Fully composed URL path. */
    path: z.string(),
    /**
     * The chain of register prefixes that compose into `path`.
     * Empty in the baseline; populated by #808 (framework prefix composition).
     */
    prefixComposition: z.array(z.string()).default([]),
    /** Framework detected for this route. */
    framework: z.enum([
        'express',
        'fastify',
        'koa',
        'hapi',
        'nest',
        'next',
        'hono',
        'elysia',
        'supabase',
        'remix',
        'trpc',
        'unknown',
    ]),
    /** Source file relative to repo root. */
    filePath: z.string(),
    lineStart: z.number().int().nonnegative(),
    lineEnd: z.number().int().nonnegative(),
    /** Handler function name (if known). */
    handlerFunction: z.string().optional(),
    /** Whether the route requires authentication (best-effort). */
    requiresAuth: z.boolean().optional(),
    /** Evidence. */
    evidence: z.array(CodeEvidenceSchema).default([]),
});
// ============= Node kinds =============
export const BCGNodeKindSchema = z.enum([
    'RouteEndpoint',
    'Function',
    'DbCall',
    'ExternalCall',
]);
export const RouteEndpointNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('RouteEndpoint'),
    endpoint: RouteEndpointSchema,
});
export const BackendFunctionNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('Function'),
    functionName: z.string(),
    filePath: z.string().optional(),
    qualifiedName: z.string().optional(),
});
export const DbCallNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('DbCall'),
    /** e.g. 'select' | 'insert' | 'update' | 'delete' | 'upsert' */
    operation: z.string(),
    /** Library, e.g. 'prisma' | 'typeorm' | 'mongoose' | 'knex' | 'supabase-js' */
    library: z.string(),
    /** Table / model name if detected. */
    model: z.string().optional(),
    filePath: z.string(),
    line: z.number().int().nonnegative(),
    evidence: z.array(CodeEvidenceSchema).default([]),
});
export const ExternalCallNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('ExternalCall'),
    /** Service name, e.g. 'stripe', 'supabase.auth', 'sendgrid'. */
    service: z.string(),
    /** Method/operation invoked. */
    method: z.string(),
    filePath: z.string(),
    line: z.number().int().nonnegative(),
    evidence: z.array(CodeEvidenceSchema).default([]),
    /**
     * veodiagram-issues#37 — discriminator between in-process SDK
     * method calls (`firebaseAuth.setPersistence(...)`) and real
     * outgoing HTTP fetches (`fetch('https://portal.example.com/...')`).
     * The audit on jassems/tcc surfaced these being conflated — only
     * 1 of 10 ExternalCall nodes was a real HTTP call; the other 9
     * were SDK methods.
     *
     * `sdk` — the call is an SDK method on an imported client object.
     *   Stays in-process; the SDK may or may not make a network
     *   call internally.
     * `http` — the call is a direct HTTP fetch (fetch/axios/got/ky/etc.)
     *   to an external host. Has a URL (or partial pattern from #38).
     */
    category: z.enum(['sdk', 'http']).optional(),
    /** When category='http', the resolved or partial URL pattern. */
    urlPattern: z.string().optional(),
});
export const BCGNodeSchema = z.discriminatedUnion('kind', [
    RouteEndpointNodeSchema,
    BackendFunctionNodeSchema,
    DbCallNodeSchema,
    ExternalCallNodeSchema,
]);
// ============= Edges =============
export const BCGEdgeKindSchema = z.enum(['routes-to', 'calls']);
export const BCGEdgeSchema = z.object({
    from: z.string(),
    to: z.string(),
    kind: BCGEdgeKindSchema,
});
// ============= Stats =============
export const BCGStatsSchema = z.object({
    endpoints: z.number().int().nonnegative(),
    functions: z.number().int().nonnegative(),
    dbLeaves: z.number().int().nonnegative(),
    externalLeaves: z.number().int().nonnegative(),
    unknownFrameworkRoutes: z.number().int().nonnegative(),
});
// ============= Top-level graph =============
export const BackendCallGraphSchema = z.object({
    projectId: z.string(),
    /** Schema version. */
    version: z.literal(1),
    generatedAt: z.string(),
    /** All nodes keyed by node id. */
    nodes: z.record(z.string(), BCGNodeSchema),
    /** All edges. */
    edges: z.array(BCGEdgeSchema),
    /** Denormalized index of route endpoints for fast iteration. */
    endpoints: z.array(RouteEndpointSchema),
    /**
     * Reachability map: for each route id, the list of node ids transitively
     * reachable from that route via `calls` edges. Populated at build time
     * by a BFS walk over the function call graph. Consumers (e.g. the flow
     * diagram renderer and the autoStitcher in #811) use this to answer
     * "what happens when this endpoint is called" without re-computing.
     */
    reachability: z.record(z.string(), z.array(z.string())),
    /**
     * Cutover Phase 1 — per-node hop distance for each route's reachability
     * set: `routeId → (nodeId → hopDistance)`, where hopDistance is the
     * number of function-call hops from the route handler to the function
     * that directly contains the node (handler-body leaf = 0, one callee
     * deep = 1, …). Populated alongside `reachability` by the BFS. Optional
     * so BCG rows persisted before this field existed still validate; when
     * absent, consumers default hop distance to 0 (the pre-Phase-1
     * behaviour).
     */
    reachabilityDepth: z.record(z.string(), z.record(z.string(), z.number())).optional(),
    stats: BCGStatsSchema,
});
//# sourceMappingURL=backendCallGraph.js.map